import { useEffect, useMemo, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchSourceChannels,
  fetchSourceChannelById,
  updateSourceChannelStatus,
  clearSourceChannelDetail
} from '../store/slices/sourceChannelsSlice';

const statusOptions = [
  { label: 'All Statuses', value: 'ALL' },
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'INACTIVE', value: 'INACTIVE' }
];

const columns = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Name' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', sortable: false }
];

const statusColor = (status) => {
  const upper = (status || '').toUpperCase();
  if (upper === 'ACTIVE') return 'success';
  if (upper === 'INACTIVE') return 'default';
  return 'default';
};

const SourceChannelListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, meta, detail, detailLoading, detailError, query } = useSelector(
    (state) => state.sourceChannels
  );

  const [filters, setFilters] = useState({
    search: query?.search ?? '',
    status: query?.status ?? 'ALL'
  });
  const [sortBy, setSortBy] = useState(query?.sortBy ?? 'id');
  const [sortDir, setSortDir] = useState(query?.sortDir ?? 'desc');
  const [page, setPage] = useState(Math.max((query?.page ?? 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(query?.pageSize ?? 10);
  const [viewId, setViewId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const totalCount = meta?.totalItems ?? items.length;

  const handleSort = (columnId) => {
    if (columnId === 'actions') return;
    const isAsc = sortBy === columnId && sortDir === 'asc';
    setSortBy(columnId);
    setSortDir(isAsc ? 'desc' : 'asc');
  };

  const handleFilterChange = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    setPage(0);
  };

  useEffect(() => {
    dispatch(
      fetchSourceChannels({
        page: page + 1,
        pageSize: rowsPerPage,
        search: filters.search.trim(),
        status: filters.status,
        sortBy,
        sortDir
      })
    );
  }, [dispatch, page, rowsPerPage, filters, sortBy, sortDir]);

  useEffect(() => {
    if (!viewId) return;
    dispatch(fetchSourceChannelById(viewId));
  }, [dispatch, viewId]);

  useEffect(() => {
    return () => {
      dispatch(clearSourceChannelDetail());
    };
  }, [dispatch]);

  const viewSecret = detail?.secret_key || detail?.secretKey || '';
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (!viewId) {
      setShowSecret(false);
    }
  }, [viewId]);

  const handleCopy = () => {
    if (!viewSecret) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(viewSecret).catch(() => {
        /* ignore */
      });
    }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Source Channel</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Source Channel
          </Typography>
          <Typography color="text.secondary">
            Manage source channels for incoming requests.
          </Typography>
        </Box>
        <Button
          size="large"
          onClick={() => navigate('/source-channels/new')}
          sx={{
            alignSelf: { xs: 'stretch', md: 'center' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 240 },
            boxSizing: 'border-box'
          }}
        >
          + New Source Channel
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              placeholder="Search by name"
              value={filters.search}
              onChange={handleFilterChange('search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              fullWidth
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={handleFilterChange('status')}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={sortBy === column.id ? sortDir : false}
                    >
                      {column.sortable === false ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDir : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((item) => {
                  const nextStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>#{item.id}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{item.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.status} color={statusColor(item.status)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => setViewId(item.id)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/source-channels/${item.id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={item.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setConfirmAction({ id: item.id, status: nextStatus })
                              }
                            >
                              {item.status === 'ACTIVE' ? (
                                <ToggleOffIcon fontSize="small" />
                              ) : (
                                <ToggleOnIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          No source channels found
                        </Typography>
                        <Typography color="text.secondary">
                          Adjust filters to see available records.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Typography color="text.secondary">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              const nextRows = parseInt(event.target.value, 10);
              setRowsPerPage(nextRows);
              setPage(0);
            }}
          />
        </Stack>
      </Paper>

      <Dialog open={Boolean(viewId)} onClose={() => setViewId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Source Channel Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Typography color="text.secondary">Loading...</Typography>}
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {!detailLoading && detail && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  ID
                </Typography>
                <Typography>#{detail.id}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography>{detail.name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip label={detail.status} size="small" color={statusColor(detail.status)} />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Secret Key
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontFamily: 'monospace' }}>
                    {showSecret ? viewSecret || '-' : '*'.repeat(50)}
                  </Typography>
                  <Tooltip title={showSecret ? 'Hide Secret Key' : 'Show Secret Key'}>
                    <IconButton
                      size="small"
                      onClick={() => setShowSecret((prev) => !prev)}
                      disabled={!viewSecret}
                    >
                      {showSecret ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy Secret Key">
                    <span>
                      <IconButton size="small" onClick={handleCopy} disabled={!viewSecret}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewId(null);
              dispatch(clearSourceChannelDetail());
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to set this source channel to{' '}
            <strong>{confirmAction?.status}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!confirmAction) return;
              await dispatch(
                updateSourceChannelStatus({
                  id: confirmAction.id,
                  status: confirmAction.status
                })
              );
              setConfirmAction(null);
              dispatch(
                fetchSourceChannels({
                  page: page + 1,
                  pageSize: rowsPerPage,
                  search: filters.search.trim(),
                  status: filters.status,
                  sortBy,
                  sortDir
                })
              );
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SourceChannelListPage;
